import { SseFrame } from './types';

/**
 * Server-sent events, read from a POST response (contract §3.1; chat-ui-spec §5.4). `EventSource`
 * cannot send a POST, so the body is read as a byte stream and split into frames here.
 */

export interface SseParser {
  /** Feeds decoded text; complete frames are delivered, a trailing partial one is kept. */
  push(text: string): void;
}

/** Turns one raw frame (the text between blank lines) into a frame, or null when it has no event. */
const parseFrame = (raw: string): SseFrame | null => {
  let event = '';
  const data: string[] = [];
  raw.split('\n').forEach((line) => {
    if (!line || line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value.trim();
    else if (field === 'data') data.push(value);
  });
  if (!event) return null;
  let parsed: unknown = {};
  try {
    parsed = JSON.parse(data.join('\n'));
  } catch {
    parsed = {};
  }
  const isObject = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
  return { event, data: isObject ? (parsed as Record<string, unknown>) : {} };
};

/**
 * Splits text into frames on blank lines and carries a partial frame across chunks. Several
 * `data:` lines are joined with a newline, invalid JSON becomes `{}`, and frames without an
 * `event:` line are ignored.
 */
export function createSseParser(onFrame: (frame: SseFrame) => void): SseParser {
  let buffer = '';
  // Where the search for the next blank line resumes, so a large frame (a whole base64 image on
  // one line) arriving in many chunks is not rescanned from its start every time.
  let scanFrom = 0;
  return {
    push(text: string) {
      if (!text) return;
      buffer += text;
      if (buffer.indexOf('\r') >= 0) {
        buffer = buffer.replace(/\r\n/g, '\n');
        scanFrom = 0;
      }
      for (;;) {
        const end = buffer.indexOf('\n\n', scanFrom);
        if (end < 0) {
          scanFrom = Math.max(0, buffer.length - 1);
          return;
        }
        const raw = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        scanFrom = 0;
        const frame = parseFrame(raw);
        if (frame) onFrame(frame);
      }
    },
  };
}

/** The part of a `ReadableStreamDefaultReader` this needs; tests pass a fake. */
export interface ByteReader {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
}

export interface TextDecoderLike {
  decode(input?: Uint8Array, options?: { stream?: boolean }): string;
}

/**
 * Reads a response body to its end, delivering frames as they complete. An abort rejects the
 * pending read (fetch does this itself); the signal is checked between reads as well, so a reader
 * that ignores aborts still stops.
 */
export async function readSse(
  reader: ByteReader,
  onFrame: (frame: SseFrame) => void,
  options: { signal?: AbortSignal; decoder?: TextDecoderLike } = {},
): Promise<void> {
  const { signal } = options;
  const decoder = options.decoder ?? new TextDecoder();
  const parser = createSseParser(onFrame);
  for (;;) {
    if (signal?.aborted) throw abortError();
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (signal?.aborted) throw abortError();
    if (done) break;
    if (value) parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
}

export const abortError = (): Error => {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
};

export const isAbortError = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError';
